import React, { useState, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { addCurrency, roundCurrency } from "../../../utils/currencyUtils";
import { formatPrizeFundMoney } from "../../../utility/prizeFundEstimate";
import { useTournamentId } from "../../../context/TournamentRouteContext";

const EntriesMasthead = () => (
  <div className="bg-white text-brand-charcoal px-6 py-4 border-b border-brand-charcoal/10">
    <h1 className="font-display font-bold text-xl text-brand-charcoal tracking-tight">
      Entries
    </h1>
    <p className="text-sm text-brand-muted mt-1">
      Entry fees and added money by event. Fee columns are board totals
      (per-bowler rate × bowlers). Added money is a board total, not a
      per-bowler rate.
    </p>
  </div>
);

const PrimaryChip = () => (
  <span className="ml-2 inline-flex items-center bg-brand-charcoal text-brand-cream px-2 py-1 rounded-lg text-xs font-display font-bold">
    Primary
  </span>
);

const thLeft =
  "px-6 py-3 text-left text-xs font-display font-bold tracking-wider uppercase text-brand-charcoal bg-white";
const thRight =
  "px-6 py-3 text-right text-xs font-display font-bold tracking-wider uppercase text-brand-charcoal bg-white";

const EventEntries = () => {
  const tournamentId = useTournamentId();
  const [eventStats, setEventStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEventStats = async () => {
      try {
        // Use RPC function to get all event statistics (server-side calculation)
        const { data: eventStatsData, error: statsError } = await supabase.rpc(
          "get_event_entry_statistics",
          {
            input_tournament_id: tournamentId,
          }
        );

        if (statsError) {
          console.error("Error fetching event statistics:", statsError);
          throw statsError;
        }

        if (!eventStatsData || eventStatsData.length === 0) {
          setEventStats([]);
          setLoading(false);
          return;
        }

        // Transform the RPC response to match the expected format
        // We need to get individual fee amounts (not just totals) for the fee map
        // So we still need to fetch fees to get the per-fee amounts
        const eventIds = eventStatsData.map((stat) => stat.event_id);
        const { data: eventFees, error: feesError } = await supabase
          .from("eventFee")
          .select(
            `
            *,
            fees (
              feeName
            )
          `
          )
          .in("eventsID", eventIds);

        if (feesError) {
          console.error("Error fetching event fees:", feesError);
          throw feesError;
        }

        // Create fee map per event (fee name -> amount per entry)
        const feeMapByEvent = {};
        eventFees.forEach((fee) => {
          if (!feeMapByEvent[fee.eventsID]) {
            feeMapByEvent[fee.eventsID] = {};
          }
          feeMapByEvent[fee.eventsID][fee.fees.feeName] = fee.amount;
        });

        // Transform to expected format
        // bowler_count is already correct: unique bowlers for all events, total entries for regular events
        const statsArray = eventStatsData.map((stat) => ({
          eventName: stat.event_name,
          isPrimary: stat.is_primary,
          fees: feeMapByEvent[stat.event_id] || {},
          bowlerCount: stat.bowler_count, // Unique bowlers for all events, total entries for regular
          additionalPrizeFund: stat.additional_prize_fund || 0,
          totals: stat.fee_totals || {},
        }));

        setEventStats(statsArray);
      } catch (err) {
        console.error("Error in fetchEventStats:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (tournamentId) {
      fetchEventStats();
    } else {
      setLoading(false);
    }
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="card-board overflow-hidden">
        <EntriesMasthead />
        <div className="p-8">
          <div className="space-y-3 animate-pulse">
            <div className="h-4 bg-brand-charcoal/10 rounded w-full" />
            <div className="h-4 bg-brand-charcoal/10 rounded w-2/3" />
            <div className="h-4 bg-brand-charcoal/10 rounded w-1/2" />
            <div className="h-4 bg-brand-charcoal/10 rounded w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-board overflow-hidden">
        <EntriesMasthead />
        <div className="p-8 text-center">
          <p className="text-red-600 text-sm">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (eventStats.length === 0) {
    return (
      <div className="card-board overflow-hidden">
        <EntriesMasthead />
        <div className="p-8 text-center">
          <h3 className="heading-display text-lg mb-2">No entries posted</h3>
          <p className="text-brand-muted">
            No event entries found for this tournament.
          </p>
        </div>
      </div>
    );
  }

  // Get unique fee types across all events
  const allFeeTypes = [
    ...new Set(eventStats.flatMap((event) => Object.keys(event.fees))),
  ].sort();

  // Calculate grand totals
  const grandTotals = allFeeTypes.reduce((acc, feeType) => {
    acc[feeType] = eventStats.reduce(
      (sum, event) => addCurrency(sum, event.totals[feeType] || 0),
      0
    );
    return acc;
  }, {});

  const totalBowlers = eventStats.reduce(
    (sum, event) => sum + event.bowlerCount,
    0
  );
  const totalRevenue = Object.values(grandTotals).reduce(
    (sum, amount) => addCurrency(sum, amount),
    0
  );

  const extraPrizeFund = eventStats.reduce(
    (sum, event) => addCurrency(sum, event.additionalPrizeFund || 0),
    0
  );
  const grandRevenue = roundCurrency(addCurrency(totalRevenue, extraPrizeFund));

  const eventTotal = (event) =>
    roundCurrency(
      addCurrency(
        Object.values(event.totals).reduce(
          (sum, amount) => addCurrency(sum, amount),
          0
        ),
        event.additionalPrizeFund || 0
      )
    );

  return (
    <div className="card-board overflow-hidden">
      <EntriesMasthead />

      <div className="block md:hidden p-4 space-y-3">
        {eventStats.map((event) => (
          <div key={event.eventName} className="card-inset p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center flex-wrap">
                <h3 className="font-display font-bold text-brand-charcoal">
                  {event.eventName}
                </h3>
                {event.isPrimary && <PrimaryChip />}
              </div>
              <div className="text-right">
                <div className="font-score text-lg font-semibold text-brand-charcoal">
                  ${eventTotal(event).toFixed(2)}
                </div>
                <div className="text-sm text-brand-muted">
                  {event.bowlerCount}{" "}
                  {event.bowlerCount === 1 ? "bowler" : "bowlers"}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {event.additionalPrizeFund > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-brand-muted">Added prize fund (board total):</span>
                  <span className="font-score text-brand-charcoal">
                    ${event.additionalPrizeFund.toFixed(2)}
                  </span>
                </div>
              )}
              {Object.entries(event.fees).map(([feeType, rate]) => (
                <div key={feeType} className="flex justify-between text-sm gap-3">
                  <span className="text-brand-muted">{feeType} board total:</span>
                  <span className="text-right">
                    {event.totals[feeType] ? (
                      <span className="font-score text-brand-charcoal">
                        ${event.totals[feeType].toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-brand-muted">N/A</span>
                    )}
                    {Number(rate) > 0 ? (
                      <span className="block text-xs font-medium text-brand-charcoal">
                        {formatPrizeFundMoney(rate)} per bowler
                      </span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="card-inset p-4">
          <h3 className="heading-display text-lg mb-3">Totals</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-brand-muted">Total Bowlers:</span>
              <span className="font-score text-brand-charcoal">
                {totalBowlers}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-brand-muted">
                Total Additional Prize Fund:
              </span>
              <span className="font-score text-brand-charcoal">
                ${roundCurrency(extraPrizeFund).toFixed(2)}
              </span>
            </div>
            {allFeeTypes.map((feeType) => (
              <div key={feeType} className="flex justify-between text-sm">
                <span className="text-brand-muted">Total {feeType}:</span>
                <span className="font-score text-brand-charcoal">
                  ${grandTotals[feeType].toFixed(2)}
                </span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-semibold border-t border-brand-charcoal/10 pt-2 mt-2">
              <span className="text-brand-charcoal">Total Revenue:</span>
              <span className="font-score text-brand-charcoal">
                ${grandRevenue.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-brand-charcoal/10">
          <thead className="bg-white border-b border-brand-charcoal/10">
            <tr>
              <th className={thLeft}>Event</th>
              <th className={thRight}>Bowlers</th>
              <th className={thRight}>
                <div>Added Prize Fund</div>
                <div className="mt-0.5 text-[11px] font-medium normal-case tracking-normal text-brand-muted">
                  Board total
                </div>
              </th>
              {allFeeTypes.map((feeType) => (
                <th key={feeType} className={thRight}>
                  <div>{feeType}</div>
                  <div className="mt-0.5 text-[11px] font-medium normal-case tracking-normal text-brand-muted">
                    Board total
                  </div>
                </th>
              ))}
              <th className={thRight}>Total</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-brand-charcoal/10">
            {eventStats.map((event, index) => (
              <tr
                key={event.eventName}
                className={index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-brand-charcoal">
                      {event.eventName}
                    </div>
                    {event.isPrimary && <PrimaryChip />}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-score text-brand-charcoal">
                  {event.bowlerCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-score text-brand-charcoal">
                  ${(event.additionalPrizeFund || 0).toFixed(2)}
                </td>
                {allFeeTypes.map((feeType) => (
                  <td
                    key={feeType}
                    className="px-6 py-4 whitespace-nowrap text-right text-sm text-brand-charcoal"
                  >
                  {event.totals[feeType] ? (
                    <div className="font-score">
                      ${event.totals[feeType].toFixed(2)}
                    </div>
                  ) : (
                    <span className="text-brand-muted">N/A</span>
                  )}
                  {Number(event.fees[feeType]) > 0 ? (
                    <div className="text-xs font-medium text-brand-charcoal">
                      {formatPrizeFundMoney(event.fees[feeType])} per bowler
                    </div>
                  ) : null}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-score font-semibold text-brand-charcoal">
                  ${eventTotal(event).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-brand-cream">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-display font-bold tracking-wider uppercase text-brand-charcoal">
                Totals
              </th>
              <th className="px-6 py-3 text-right text-xs font-score font-bold text-brand-charcoal">
                {totalBowlers}
              </th>
              <th className="px-6 py-3 text-right text-xs font-score font-bold text-brand-charcoal">
                ${roundCurrency(extraPrizeFund).toFixed(2)}
              </th>
              {allFeeTypes.map((feeType) => (
                <th
                  key={feeType}
                  className="px-6 py-3 text-right text-xs font-score font-bold text-brand-charcoal"
                >
                  ${grandTotals[feeType].toFixed(2)}
                </th>
              ))}
              <th className="px-6 py-3 text-right text-xs font-score font-bold text-brand-charcoal">
                ${grandRevenue.toFixed(2)}
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default EventEntries;
